import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  attributeDoesMatchFilter,
  trackItemSelected,
} from "~/components/shared/StepsEditorSidebar/Guide/utils";

export type Content = {
  [key: string]: ContentCategory;
};

// Top-level categories - ie "Steps" / "Notify"
export type ContentCategory = {
  name: string;
  textContent: string;
  attributes: Array<ContentItem>;
};

// ie "Command" / "Wait" / "Block"
export type ContentItem = {
  name: string;
  shortDescription: string;
  docsURL?: string;
  textContent: string;
  attributes: Array<ContentAttribute>;
};

// ie "Soft Fail" / "Email"
export type ContentAttribute = {
  name: string;
  isRequired?: boolean;
  textContent: string;
  docsURL?: string;
};

const GuidesContext = createContext<{
  loading: boolean;
  error: boolean;
  content: ContentCategory[];
  selectedItem: ContentItem | null;
  selectedAttribute: ContentAttribute | null;
  onSelectItem: (contentItem: ContentItem, contentAttribute?: ContentAttribute) => void;
  onClearItem: () => void;
  onFilterChange: (filter: string) => void;
  onReload: () => void;
  filter: string;
  matchedContent: Array<{
    item: ContentItem;
    attribute: ContentAttribute;
  }>;
} | null>(null);

export const GuidesProvider = ({ trackingContext, children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [content, setContent] = useState<Content | null>(null);
  const [filter, setFilter] = useState("");

  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [selectedAttribute, setSelectedAttribute] = useState<ContentAttribute | null>(null);

  const onSelectItem = useCallback(
    (contentItem: ContentItem, contentAttribute?: ContentAttribute) => {
      trackItemSelected(contentItem, trackingContext);

      setSelectedItem(contentItem);

      if (contentAttribute) {
        setSelectedAttribute(contentAttribute);
      }
    },
    [],
  );

  const onClearItem = useCallback(() => {
    setSelectedItem(null);
    setSelectedAttribute(null);
  }, []);

  const matchedContent = useMemo(() => {
    if (!filter || !content) {
      return [];
    }

    return Object.values(content)
      .flatMap((contentCategory) => contentCategory.attributes)
      .flatMap((item) =>
        item.attributes
          .filter((attribute) => attributeDoesMatchFilter(attribute, filter))
          .map((attribute) => ({ item, attribute })),
      );
  }, [filter, content]);

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError(false);

    // Allow fetching the data from the local dev environment
    // if we're running in development and add ?local-docs to the URL
    const host =
      process.env.NODE_ENV === "development" &&
      window.location.search &&
      window.location.search.includes("local-docs")
        ? "http://localhost:3000"
        : "https://buildkite.com";

    try {
      const response = await fetch(`${host}/docs/quick-reference/pipelines.json`);
      const content = await response.json();

      setContent(content);
      setLoading(false);
    } catch (error) {
      setError(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, []);

  const contextValue = useMemo(
    () => ({
      content: content ? Object.values(content) : [],
      onReload: loadContent,
      onFilterChange: setFilter,
      onSelectItem,
      onClearItem,
      loading,
      error,
      matchedContent,
      selectedItem,
      selectedAttribute,
      filter,
    }),
    [
      content,
      loadContent,
      onSelectItem,
      onClearItem,
      loading,
      error,
      matchedContent,
      selectedItem,
      selectedAttribute,
      filter,
    ],
  );

  return <GuidesContext.Provider value={contextValue}>{children}</GuidesContext.Provider>;
};

export const useGuides = () => {
  const context = useContext(GuidesContext);

  if (!context) {
    throw new Error("useGuides must be used within a GuidesProvider");
  }

  return context;
};
